import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HEADER_ALIASES, MAX_CODE_LENGTH, MAX_PASTE_CHARS, MAX_PDF_BYTES, MAX_STATEMENT_LENGTH, MAX_UPLOADED_ROWS, normalizeRows, parseStandardsCsv, toUploadedRow, unverifiedRows, type CsvProblem, type UploadFormat } from '../../core/uploaded-standards';
import type { PreviewAction } from '../../core/events';
import type { UploadedStandardRow } from '../../core/types';
import { accessRevoked, call, toApiError, type ApiError, type Allowance, type GetToken, type StandardSet } from '../api';
import { Dialog } from './Dialog';

/**
 * A teacher's own standards: add them, check them, save them, and take them away again.
 *
 * Nothing is stored until the teacher confirms the review (R6). CSV is parsed here in the
 * browser with the same core rules the Worker saves through, so it costs no extraction;
 * pasted text and PDFs go to the model and use one of the weekly uploads. Every way out of
 * unsaved work, and every replace or delete, asks first, inside the dialog.
 */
type Tab = UploadFormat;
const TABS: { value: Tab; label: string }[] = [{ value: 'paste', label: 'Paste text' }, { value: 'csv', label: 'CSV file' }, { value: 'pdf', label: 'PDF file' }];
const FORMAT_LABEL: Record<UploadFormat, string> = { csv: 'a CSV file', paste: 'pasted text', pdf: 'a PDF' };
/** The extraction is one long request, so like `AskProgress` this says only what is true: it is still reading. */
const SLOW_AFTER = 45000;
interface ReviewRow extends UploadedStandardRow { key: number; unverified: boolean }
/** `edit` is the saved set reopened with Edit rows; anything else replaces the saved set. */
interface Review { format: UploadFormat; rows: ReviewRow[]; truncated: boolean; problems: CsvProblem[]; extracted: boolean; edit: boolean }
type View = 'loading' | 'failed' | 'current' | 'choose' | 'extracting' | 'review';
type Confirm = { kind: 'discard'; then: 'close' | 'back' } | { kind: 'replace' } | { kind: 'delete' } | null;

const plural = (n: number, one: string, many: string) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const when = (iso: string) => new Date(iso).toLocaleString();
const squash = (value: string) => value.replace(/\s+/g, ' ').trim();
/** Whether `normalizeRows` will keep this row as typed, cut it, or drop it on save. */
function rowNote(row: UploadedStandardRow): 'dropped' | 'trimmed' | null {
  const result = normalizeRows([row]);
  if (!result.ok) return 'dropped';
  const kept = result.rows[0]!;
  return kept.code !== squash(row.code) || kept.statement !== squash(row.statement) ? 'trimmed' : null;
}
function problemText({ line, reason }: CsvProblem): string {
  switch (reason) {
    case 'empty_file': return 'This file has no rows.';
    case 'missing_code_column': return `No code column. Name one column ${HEADER_ALIASES.code.join(', ')}.`;
    case 'missing_statement_column': return `No statement column. Name one column ${HEADER_ALIASES.statement.join(', ')}.`;
    case 'missing_code': return `Line ${line}: no code, so this line was skipped.`;
    case 'missing_statement': return `Line ${line}: no statement, so this line was skipped.`;
  }
}
function readFile(file: File, as: 'text' | 'base64'): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => { const result = String(reader.result ?? ''); resolve(as === 'text' ? result : result.slice(result.indexOf(',') + 1)); };
    reader.onerror = () => reject(reader.error);
    if (as === 'text') reader.readAsText(file); else reader.readAsDataURL(file);
  });
}
function extractionMessage(e: ApiError): string {
  if (e.status === 429) return `You have used your extractions for this week.${e.nextAvailableAt ? ` Your next one is available ${when(e.nextAvailableAt)}.` : ''} A CSV file does not use one — choose CSV file above.`;
  if (e.reason === 'providers_not_configured') return 'Reading documents is not available right now. A CSV file still works.';
  if (e.reason === 'pdf_too_large' || e.status === 413) return 'This file is too large. The limit is 10 MB. Split it into smaller files or use a CSV file.';
  if (e.reason === 'text_too_long') return `Paste up to ${MAX_PASTE_CHARS.toLocaleString()} characters at a time.`;
  if (e.reason === 'invalid_pdf') return 'This file could not be read as a PDF. Try another copy or use a CSV file.';
  if (e.reason === 'model_refusal') return 'We could not read standards from this document. Try a CSV file.';
  if (e.status >= 500 || e.status === 0) return 'Reading this document did not finish. Try again, split the document into smaller parts, or use a CSV file.';
  return e.message;
}

export function StandardsUpload({ getToken, uploadsEnabled, allowance, onCountChange, onPaused, onUsageChange, onAccessDenied, onTrack, onClose }: {
  getToken: GetToken; uploadsEnabled: boolean; allowance: Allowance | null;
  /** The saved set's size after a load, save or delete, for the "Your standards" line. */
  onCountChange(count: number): void;
  /** The server refused with `uploads_disabled`: the switch went on while the panel was open. */
  onPaused(): void;
  onUsageChange(): void;
  onAccessDenied(error: ApiError): void;
  onTrack(action: PreviewAction, properties?: Record<string, string | number | boolean | string[]>): void;
  onClose(): void;
}) {
  const [view, setView] = useState<View>('loading');
  const [set, setSet] = useState<StandardSet | null>(null);
  const [paused, setPaused] = useState(!uploadsEnabled);
  const [tab, setTab] = useState<Tab>('paste');
  const [text, setText] = useState('');
  const [pdf, setPdf] = useState<File | null>(null);
  const [review, setReview] = useState<Review | null>(null);
  const [confirm, setConfirm] = useState<Confirm>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [empty, setEmpty] = useState(false);
  const [working, setWorking] = useState(false);
  const live = useRef(true);
  const nextKey = useRef(0);
  const extraction = useRef<AbortController | null>(null);
  const tabs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});
  const callbacks = useRef({ onCountChange, onPaused, onAccessDenied }); callbacks.current = { onCountChange, onPaused, onAccessDenied };
  const pausedRef = useRef(paused); pausedRef.current = paused;

  /** The error to show, or null when the failure leaves the panel's flow: a revoked tool or paused uploads. */
  const divert = useCallback((failure: unknown): ApiError | null => {
    const e = toApiError(failure);
    if (accessRevoked(e.reason)) { callbacks.current.onAccessDenied(e); return null; }
    if (e.reason === 'uploads_disabled') { setPaused(true); callbacks.current.onPaused(); return null; }
    return e;
  }, []);
  const load = useCallback(async () => {
    setView('loading'); setMessage(null);
    try {
      const result = await call<{ set: StandardSet | null }>(getToken, '/api/standards');
      if (!live.current) return;
      setSet(result.set); callbacks.current.onCountChange(result.set?.rowCount ?? 0);
      setView(result.set || pausedRef.current ? 'current' : 'choose');
    } catch (failure) {
      if (!live.current) return;
      const e = divert(failure); setMessage(e ? e.message : null); setView('failed');
    }
  }, [getToken, divert]);
  useEffect(() => { live.current = true; void load(); return () => { live.current = false; extraction.current?.abort(); }; }, [load]);

  function openReview(next: Review) {
    setReview(next);
    setEmpty(false); setMessage(null); setView('review');
    onTrack('standards_review_opened', { format: next.format, row_count: next.rows.length });
  }
  const toRows = (rows: ReadonlyArray<UploadedStandardRow>, flagged = new Set<number>()): ReviewRow[] =>
    rows.map((row, i) => ({ ...row, key: nextKey.current++, unverified: flagged.has(i) }));

  function chooseTab(next: Tab, focus = false) {
    if (next !== tab) onTrack('standards_source_selected', { source: 'upload', format: next });
    setTab(next); setMessage(null); setEmpty(false);
    if (focus) tabs.current[next]?.focus();
  }
  /** Named reasons only; never a code, a statement or a file name. */
  function failed(stage: 'csv' | 'pdf' | 'extract' | 'save', format: UploadFormat, reason: string, status?: number) {
    onTrack('standards_upload_failed', { stage, format, reason, ...(status === undefined ? {} : { status }) });
  }
  async function chooseCsv(file: File | undefined) {
    if (!file) return;
    setMessage(null); setEmpty(false);
    let contents: string;
    try { contents = await readFile(file, 'text'); } catch { failed('csv', 'csv', 'unreadable_file'); setMessage('This file could not be read. Try another copy.'); return; }
    const parsed = parseStandardsCsv(contents);
    if (!parsed.rows.length) { failed('csv', 'csv', parsed.problems[0]?.reason ?? 'empty_file'); setMessage(parsed.problems.map(problemText).join(' ')); return; }
    openReview({ format: 'csv', rows: toRows(parsed.rows), truncated: false, problems: parsed.problems, extracted: false, edit: false });
  }
  function choosePdf(file: File | undefined) {
    setEmpty(false); setPdf(null); setMessage(null);
    if (!file) return;
    if (file.size > MAX_PDF_BYTES) { failed('pdf', 'pdf', 'pdf_too_large'); setMessage(`This PDF is ${(file.size / 1048576).toFixed(1)} MB. The limit is 10 MB. Split it into smaller files or use a CSV file.`); return; }
    setPdf(file);
  }
  async function extract(kind: 'paste' | 'pdf') {
    const controller = new AbortController(); extraction.current = controller;
    setView('extracting'); setMessage(null); setEmpty(false);
    try {
      const body = kind === 'paste' ? { kind: 'text', text } : { kind: 'pdf', pdfBase64: await readFile(pdf!, 'base64') };
      const result = await call<{ rows: UploadedStandardRow[]; truncated: boolean }>(getToken, '/api/standards/extract', { method: 'POST', body: JSON.stringify(body), signal: controller.signal });
      if (controller.signal.aborted || !live.current) return;
      if (!result.rows.length) { setEmpty(true); setView('choose'); return; }
      openReview({ format: kind, rows: toRows(result.rows, kind === 'paste' ? new Set(unverifiedRows(result.rows, text)) : undefined), truncated: result.truncated, problems: [], extracted: true, edit: false });
    } catch (failure) {
      if (controller.signal.aborted || !live.current) return;
      const e = divert(failure);
      if (e) { failed('extract', kind, e.reason, e.status); setMessage(extractionMessage(e)); setView('choose'); } else setView('current');
    } finally {
      if (extraction.current === controller) extraction.current = null;
      onUsageChange();
    }
  }
  function editRow(key: number, change: Partial<UploadedStandardRow>) {
    setReview(current => current && { ...current, rows: current.rows.map(row => row.key === key ? { ...row, ...change } : row) });
  }
  function removeRow(key: number) {
    setReview(current => current && { ...current, rows: current.rows.filter(row => row.key !== key) });
  }
  async function save() {
    if (!review) return;
    setConfirm(null); setWorking(true); setMessage(null);
    try {
      const rows = review.rows.map(({ code, statement, gradeLevels, subject }) => toUploadedRow(code, statement, gradeLevels, subject));
      await call(getToken, '/api/standards', { method: 'PUT', body: JSON.stringify({ format: review.format, rows }) });
      if (!live.current) return;
      setReview(null); setText(''); setPdf(null);
      await load();
    } catch (failure) {
      if (!live.current) return;
      const e = divert(failure);
      if (e) failed('save', review.format, e.reason, e.status);
      setMessage(e ? (e.reason === 'too_many_rows' ? `A set holds at most ${MAX_UPLOADED_ROWS.toLocaleString()} standards.` : e.reason === 'no_rows' ? 'There are no complete rows to save.' : e.message) : 'Uploads are paused right now, so these rows could not be saved.');
    } finally { if (live.current) setWorking(false); }
  }
  async function remove() {
    setConfirm(null); setWorking(true); setMessage(null);
    try {
      await call(getToken, '/api/standards', { method: 'DELETE' });
      if (!live.current) return;
      setSet(null); callbacks.current.onCountChange(0); setView(pausedRef.current ? 'current' : 'choose');
    } catch (failure) {
      if (!live.current) return;
      const e = divert(failure); if (e) setMessage(e.message);
    } finally { if (live.current) setWorking(false); }
  }
  /** Leaving with work in hand asks first; leaving with nothing to lose just goes. */
  function leave(then: 'close' | 'back') {
    if (view === 'extracting' || (view === 'review' && review?.rows.length)) { setConfirm({ kind: 'discard', then }); return; }
    finishLeaving(then);
  }
  function finishLeaving(then: 'close' | 'back') {
    // Most teachers who open the panel close it without trying anything; this says from where.
    if (then === 'close') onTrack('standards_panel_closed', { view, tab, has_input: Boolean(text.trim() || pdf), has_set: Boolean(set) });
    extraction.current?.abort(); extraction.current = null;
    setConfirm(null); setReview(null); setMessage(null);
    // Back from the chooser or from Edit rows is the saved set; back from new rows is the chooser.
    if (then === 'close') onClose(); else setView(set && (view === 'choose' || review?.edit) ? 'current' : 'choose');
  }

  const remaining = allowance?.remaining;
  const exhausted = remaining === 0;
  const counter = allowance && <p className="muted upload-allowance">{remaining} of {allowance.limit} extractions left this week{exhausted && allowance.nextAvailableAt && <><br />Next extraction available {when(allowance.nextAvailableAt)}</>}</p>;
  // Both run every row through `normalizeRows`, so they wait for the rows to change rather than every keystroke elsewhere.
  const reviewRows = review?.rows;
  const rowsSavable = useMemo(() => Boolean(reviewRows && normalizeRows(reviewRows).ok), [reviewRows]);
  const notes = useMemo(() => new Map(reviewRows?.map(row => [row.key, rowNote(row)])), [reviewRows]);
  const canSave = rowsSavable && !working && !paused;

  function body() {
    if (confirm?.kind === 'discard') {
      const rows = review?.rows.length ?? 0;
      return <div className="standards-confirm" role="group" aria-labelledby="standards-confirm-title">
        <h3 id="standards-confirm-title">{view === 'extracting' ? 'Stop reading this document?' : rows === 1 ? 'Discard this row?' : `Discard these ${rows.toLocaleString()} rows?`}</h3>
        {(view === 'extracting' || review?.extracted) && <p className="muted">This extraction used one of your weekly uploads.</p>}
        <div className="dialog-actions"><button type="button" className="secondary" onClick={() => setConfirm(null)}>{view === 'extracting' ? 'Keep reading' : 'Keep reviewing'}</button><button type="button" className="primary destructive" onClick={() => finishLeaving(confirm.then)}>Discard</button></div>
      </div>;
    }
    if (confirm?.kind === 'replace' && review && set) return <div className="standards-confirm" role="group" aria-labelledby="standards-confirm-title">
      <h3 id="standards-confirm-title">Replace your {plural(set.rowCount, 'saved standard', 'saved standards')} with {review.rows.length === 1 ? 'this one' : `these ${review.rows.length.toLocaleString()}`}?</h3>
      <p className="muted">Your saved set is replaced in full. Later asks search only the new rows.</p>
      <div className="dialog-actions"><button type="button" className="secondary" onClick={() => setConfirm(null)}>Keep reviewing</button><button type="button" className="primary" onClick={() => void save()}>Replace standards</button></div>
    </div>;
    if (confirm?.kind === 'delete' && set) return <div className="standards-confirm" role="group" aria-labelledby="standards-confirm-title">
      <h3 id="standards-confirm-title">Delete your {plural(set.rowCount, 'standard', 'standards')}?</h3>
      <p className="muted">Your asks will use only your state’s official standards. This cannot be undone.</p>
      <div className="dialog-actions"><button type="button" className="secondary" onClick={() => setConfirm(null)}>Keep them</button><button type="button" className="primary destructive" onClick={() => void remove()}>Delete standards</button></div>
    </div>;
    if (view === 'loading') return <p className="muted" role="status">Loading your standards…</p>;
    if (view === 'failed') return <div><p role="alert" className="error-message">{message ?? 'Your standards could not be loaded.'}</p><button type="button" className="secondary" onClick={() => void load()}>Try again</button></div>;
    if (view === 'current') return <div>
      {paused && <p className="notice upload-paused" role="status">Uploads are paused. {set ? `Your ${plural(set.rowCount, 'standard is', 'standards are')} saved but not used in asks right now. You can still view or delete ${set.rowCount === 1 ? 'it' : 'them'}.` : 'You cannot add standards right now. Asking still works.'}</p>}
      {set && <>
        <p className="standards-summary">{plural(set.rowCount, 'standard', 'standards')} · from {FORMAT_LABEL[set.format]} · updated {new Date(set.updatedAt).toLocaleDateString()}</p>
        <ol className="saved-standards ph-mask" aria-label="Your saved standards">{set.rows.map(row => <li key={row.standardId}><span className="standard-code">{row.code}</span><span>{row.statement}</span></li>)}</ol>
        {message && <p className="error-message" role="alert">{message}</p>}
        <div className="dialog-actions">
          {!paused && <><button type="button" className="secondary" disabled={working} onClick={() => openReview({ format: set.format, rows: toRows(set.rows.map(({ code, statement, gradeLevels, subject }) => ({ code, statement, gradeLevels, subject }))), truncated: false, problems: [], extracted: false, edit: true })}>Edit rows</button>
          <button type="button" className="secondary" disabled={working} onClick={() => { setMessage(null); setView('choose'); }}>Replace</button></>}
          <button type="button" className="secondary danger" disabled={working} onClick={() => setConfirm({ kind: 'delete' })}>Delete</button>
        </div>
      </>}
    </div>;
    if (view === 'review' && review) return reviewBody(review);
    return chooser();
  }

  function chooser() {
    const extracting = view === 'extracting';
    return <>
      <div className="report-tabs standards-tabs" role="tablist" aria-label="How to add your standards">{TABS.map(({ value, label }) => <button key={value} type="button" ref={node => { tabs.current[value] = node; }} role="tab" id={`standards-tab-${value}`} aria-controls={`standards-panel-${value}`} aria-selected={tab === value} tabIndex={tab === value ? 0 : -1} disabled={extracting} onClick={() => chooseTab(value)} onKeyDown={event => {
        if (['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) {
          event.preventDefault(); const index = TABS.findIndex(t => t.value === value);
          chooseTab(event.key === 'Home' ? TABS[0]!.value : event.key === 'End' ? TABS.at(-1)!.value : TABS[(index + (event.key === 'ArrowRight' ? 1 : TABS.length - 1)) % TABS.length]!.value, true);
        }
      }}>{label}</button>)}</div>
      <div role="tabpanel" id={`standards-panel-${tab}`} aria-labelledby={`standards-tab-${tab}`} className="standards-panel">
        {extracting ? <ExtractProgress /> : <>
          {tab === 'paste' && <>
            <label htmlFor="standards-paste">Paste your standards</label>
            <p className="muted" id="standards-paste-help">Copy the codes and statements from your district’s document. We copy them out as written; nothing is reworded.</p>
            <textarea id="standards-paste" className="ph-mask" aria-describedby="standards-paste-help" rows={8} maxLength={MAX_PASTE_CHARS} value={text} onChange={e => setText(e.target.value)} />
            <p className="muted upload-count">{text.length.toLocaleString()} of {MAX_PASTE_CHARS.toLocaleString()} characters</p>
            {counter}
            <button type="button" className="primary" disabled={!text.trim() || exhausted} onClick={() => void extract('paste')}>Find standards in this text</button>
          </>}
          {tab === 'csv' && <>
            <label htmlFor="standards-csv">Choose a CSV file</label>
            <p className="muted" id="standards-csv-help">One row per standard, with a code column and a statement column. Grade and subject columns are optional. Reading a CSV does not use an extraction.</p>
            <input id="standards-csv" type="file" accept=".csv,text/csv" aria-describedby="standards-csv-help" onChange={e => { void chooseCsv(e.target.files?.[0]); e.target.value = ''; }} />
          </>}
          {tab === 'pdf' && <>
            <label htmlFor="standards-pdf">Choose a PDF file</label>
            <p className="muted" id="standards-pdf-help">Up to 10 MB. A long document may be read only in part; a CSV has no such limit.</p>
            <input id="standards-pdf" type="file" accept="application/pdf,.pdf" aria-describedby="standards-pdf-help" onChange={e => choosePdf(e.target.files?.[0])} />
            {counter}
            <button type="button" className="primary" disabled={!pdf || exhausted} onClick={() => void extract('pdf')}>Find standards in this PDF</button>
          </>}
          {empty && <p className="notice" role="status">No standards found in this document. If it lists codes and statements, try a CSV file instead.</p>}
          {message && <p className="error-message" role="alert">{message}</p>}
          {set && <button type="button" className="text-button" onClick={() => leave('back')}>Back to your saved standards</button>}
        </>}
      </div>
    </>;
  }

  function reviewBody(current: Review) {
    const over = current.rows.length - MAX_UPLOADED_ROWS;
    return <div className="standards-review">
      <p className="muted">Check each code and statement against your document. Edit or remove any row. Nothing is saved until you save.</p>
      {current.truncated && <p className="notice" role="status">The rest of the document was not read. These are the rows found before reading stopped. To get the rest, split it into smaller parts or use a CSV file.</p>}
      {current.problems.length > 0 && <ul className="csv-problems" aria-label="Lines that were skipped">{current.problems.map(problem => <li key={`${problem.line}-${problem.reason}`}>{problemText(problem)}</li>)}</ul>}
      {over > 0 && <p className="error-message" role="alert">A set holds at most {MAX_UPLOADED_ROWS.toLocaleString()} standards. Remove {plural(over, 'row', 'rows')} or split the file.</p>}
      <ol className="review-rows ph-mask" aria-label="Rows to save">{current.rows.map((row, index) => {
        const n = index + 1; const note = notes.get(row.key) ?? null;
        return <li key={row.key} className={note ? `review-row ${note}` : 'review-row'}>
          <span className="review-number" aria-hidden="true">{n}</span>
          <input type="text" aria-label={`Code, row ${n}`} value={row.code} onChange={e => editRow(row.key, { code: e.target.value })} />
          <textarea aria-label={`Statement, row ${n}`} rows={2} value={row.statement} onChange={e => editRow(row.key, { statement: e.target.value })} />
          <button type="button" className="text-button danger" aria-label={`Remove ${row.code.trim() || `row ${n}`}`} onClick={() => removeRow(row.key)}>Remove</button>
          {(row.gradeLevels?.length || row.subject) && <p className="review-meta">{[row.gradeLevels?.length ? `Grade ${row.gradeLevels.join(', ')}` : '', row.subject ?? ''].filter(Boolean).join(' · ')}</p>}
          {row.unverified && <p className="review-flag">Not found in your text</p>}
          {note === 'dropped' && <p className="review-flag">Needs a code and a statement. This row will not be saved.</p>}
          {note === 'trimmed' && <p className="review-flag">Too long. Codes keep {MAX_CODE_LENGTH} characters and statements {MAX_STATEMENT_LENGTH.toLocaleString()} when saved.</p>}
        </li>;
      })}</ol>
      {message && <p className="error-message" role="alert">{message}</p>}
      <div className="dialog-actions">
        <button type="button" className="secondary" onClick={() => leave('back')}>{current.edit ? 'Cancel' : 'Start over'}</button>
        <button type="button" className="primary" disabled={!canSave} onClick={() => set && !current.edit ? setConfirm({ kind: 'replace' }) : void save()}>{working ? 'Saving…' : `Save ${plural(current.rows.length, 'standard', 'standards')}`}</button>
      </div>
    </div>;
  }

  return <Dialog titleId="standards-upload-title" className="standards-dialog" onClose={() => leave('close')}>
    <div className="topic-panel-header"><div><div className="eyebrow">YOUR STANDARDS</div><h2 id="standards-upload-title">{view === 'review' ? 'Review your standards' : 'Your standards'}</h2></div><button type="button" className="close-button" aria-label="Close" onClick={() => leave('close')}>×</button></div>
    <div className="standards-body">{body()}</div>
  </Dialog>;
}

function ExtractProgress() {
  const [slow, setSlow] = useState(false);
  useEffect(() => { const timer = window.setTimeout(() => setSlow(true), SLOW_AFTER); return () => window.clearTimeout(timer); }, []);
  return <div className="ask-progress standards-progress" role="status" aria-label="Reading your document">
    <p className="ask-progress-title">Reading your document<span className="title-dot">.</span></p>
    <p className="muted">{slow ? 'Still reading. Long documents can take up to a minute and a half.' : 'Keep this window open while we read it.'}</p>
  </div>;
}
